import { deriveDoorGeometry } from './door-model';
import type { ConfiguratorState } from './configurator-types';

export type ConfiguratorMeasurementId =
  | 'finished-width'
  | 'finished-height'
  | 'finished-thickness'
  | 'handle-length'
  | 'handle-offset'
  | 'handle-face-width'
  | 'handle-depth'
  | 'handle-transition'
  | 'handle-roundness'
  | 'handle-profile-width'
  | 'first-cnc-pass'
  | 'edge-size'
  | 'corner-radius'
  | 'stock-width'
  | 'stock-height'
  | 'stock-thickness';

export type ConfiguratorMeasurement = {
  id: ConfiguratorMeasurementId;
  label: string;
  valueText: string;
  from: string;
  to: string;
  coordinateText: string;
  viewLabel: string;
  exactAnchors: boolean;
  note?: string;
};

export type DoorDimensionSpans = {
  finishedWidth: [number, number];
  finishedHeight: [number, number];
  finishedThickness: [number, number];
  handleLength: [number, number];
  handleOffset: [number, number];
  handleFaceWidth: [number, number];
  handleDepth: [number, number];
  handleTransition: [number, number];
  firstCncPass: [number, number];
  remainingWall: [number, number];
};

export function deriveDoorDimensionSpans(config: ConfiguratorState): DoorDimensionSpans {
  const geometry = deriveDoorGeometry(config);
  const transitionStart = geometry.handle.capLeft
    ? geometry.handle.startMm
    : geometry.handle.endMm - geometry.handle.transitionLengthMm;
  const transitionEnd = geometry.handle.capLeft
    ? geometry.handle.startMm + geometry.handle.transitionLengthMm
    : geometry.handle.endMm;
  const firstPassDepthMm = Math.max(0, geometry.handle.depthMm - config.handle.roughingAllowanceMm);

  return {
    finishedWidth: [0, geometry.finished.widthMm],
    finishedHeight: [0, geometry.finished.heightMm],
    finishedThickness: [0, geometry.finished.thicknessMm],
    handleLength: [geometry.handle.startMm, geometry.handle.endMm],
    handleOffset: [0, geometry.handle.startMm],
    handleFaceWidth: [
      geometry.finished.heightMm,
      geometry.finished.heightMm - geometry.handle.faceWidthMm,
    ],
    handleDepth: [0, geometry.handle.depthMm],
    handleTransition: [transitionStart, transitionEnd],
    firstCncPass: [0, firstPassDepthMm],
    remainingWall: [geometry.handle.depthMm, geometry.finished.thicknessMm],
  };
}

export function isConfiguratorMeasurementAvailable(
  config: ConfiguratorState,
  id: ConfiguratorMeasurementId,
) {
  const geometry = deriveDoorGeometry(config);
  if (id === 'handle-offset') return geometry.variant === 'double-terminated';
  if (id === 'handle-transition' || id === 'handle-roundness') {
    return geometry.variant !== 'full-edge';
  }
  if (id === 'edge-size') return geometry.edge.profile !== 'square';
  return true;
}

export function getConfiguratorMeasurement(
  config: ConfiguratorState,
  id: ConfiguratorMeasurementId,
): ConfiguratorMeasurement {
  const geometry = deriveDoorGeometry(config);
  const spans = deriveDoorDimensionSpans(config);
  const transitionNote =
    geometry.variant === 'double-terminated'
      ? 'Rovnaká kóta platí aj pre prechod na druhom konci úchytu.'
      : undefined;

  switch (id) {
    case 'finished-width':
      return exactMeasurement(
        id,
        'Šírka hotového dielu',
        geometry.finished.widthMm,
        'ľavá tangentná rovina hotového dielu',
        'pravá tangentná rovina hotového dielu',
        axisText('X', spans.finishedWidth),
        'POHĽAD SPREDU',
      );
    case 'finished-height':
      return exactMeasurement(
        id,
        'Výška hotového dielu',
        geometry.finished.heightMm,
        'spodná tangentná rovina hotového dielu',
        'horná tangentná rovina hotového dielu',
        axisText('Y', spans.finishedHeight),
        'POHĽAD SPREDU',
      );
    case 'finished-thickness':
      return exactMeasurement(
        id,
        'Hrúbka hotového dielu',
        geometry.finished.thicknessMm,
        'predná plocha dielu',
        'zadná plocha dielu',
        axisText('Q', spans.finishedThickness),
        'BOČNÝ REZ Y–Q',
      );
    case 'handle-length':
      return exactMeasurement(
        id,
        'Dĺžka úchytu',
        geometry.handle.lengthMm,
        handleStartLabel(geometry),
        handleEndLabel(geometry),
        axisText('X', spans.handleLength),
        'POHĽAD SPREDU',
        'Ide o vodorovnú projekciu, nie o dĺžku zakrivenej dráhy.',
      );
    case 'handle-offset':
      return exactMeasurement(
        id,
        'Odsadenie úchytu',
        geometry.handle.offsetMm,
        'ľavá tangentná rovina hotového dielu',
        'začiatok ľavého nábehu úchytu',
        axisText('X', spans.handleOffset),
        'POHĽAD SPREDU',
      );
    case 'handle-face-width':
      return exactMeasurement(
        id,
        'Výška úchytového pásu',
        geometry.handle.faceWidthMm,
        'horná tangentná rovina dielu',
        'rovná spodná hranica frézovaného pásu',
        axisText('Y', spans.handleFaceWidth),
        'POHĽAD SPREDU',
      );
    case 'handle-depth':
      return exactMeasurement(
        id,
        'Finálna hĺbka frézovania',
        geometry.handle.depthMm,
        'predná plocha dielu',
        'najhlbšie dno finálneho frézovania',
        axisText('Q', spans.handleDepth),
        'BOČNÝ REZ Y–Q',
        `Zostatková stena pokračuje od Q=${formatNumber(geometry.handle.depthMm)} po Q=${formatNumber(geometry.finished.thicknessMm)} mm. V TCN zodpovedá kladnej hĺbke Q záporná súradnica Z.`,
      );
    case 'handle-transition':
      return exactMeasurement(
        id,
        'Dĺžka plynulého nábehu',
        geometry.handle.transitionLengthMm,
        geometry.handle.capLeft ? 'začiatok ľavého nábehu' : 'koniec rovného profilu',
        geometry.handle.capLeft ? 'začiatok rovného profilu' : 'koniec pravého nábehu',
        axisText('X', spans.handleTransition),
        'POHĽAD SPREDU',
        transitionNote,
      );
    case 'handle-roundness':
      return {
        id,
        label: 'Plynulosť nábehu',
        valueText: `${formatNumber(geometry.handle.transitionRoundness)} %`,
        from: 'percentuálny parameter r',
        to: 'tvar S-krivky medzi pevnými koncami nábehu',
        coordinateText: `r=${formatNumber(geometry.handle.transitionRoundness)} % · oblasť ${axisText('X', spans.handleTransition)}`,
        viewLabel: 'POHĽAD SPREDU',
        exactAnchors: false,
        note: 'Percento mení tvar S-krivky medzi týmito bodmi; nie je to samostatná dĺžková kóta.',
      };
    case 'handle-profile-width':
      return {
        id,
        label: 'Návrhová šírka profilu',
        valueText: `${formatNumber(geometry.handle.grooveWidthMm)} mm`,
        from: 'parameter G',
        to: 'mierka asymetrického schematického prierezu',
        coordinateText: `G=${formatNumber(geometry.handle.grooveWidthMm)} mm · bez potvrdených povrchových kotiev`,
        viewLabel: 'LOKÁLNY REZ PROFILOM',
        exactAnchors: false,
        note: 'Nie je to potvrdená kóta nástroja 1009. Jeho presný prierez v podkladoch chýba.',
      };
    case 'first-cnc-pass': {
      const firstPassDepthMm = spans.firstCncPass[1];
      return exactMeasurement(
        id,
        'Hĺbka prvého CNC prechodu',
        firstPassDepthMm,
        'predná plocha dielu',
        'dno prvého CNC prechodu',
        axisText('Q', spans.firstCncPass),
        'BOČNÝ REZ Y–Q',
        `Finálny prechod pokračuje po Q=${formatNumber(geometry.handle.depthMm)} mm. V TCN je smer Q zapísaný ako záporné Z; hotový 3D tvar sa nemení.`,
      );
    }
    case 'edge-size':
      return {
        id,
        label: config.edgeTreatment.profile === 'chamfer' ? 'Veľkosť skosenia' : 'Rádius hrany',
        valueText: `${formatNumber(geometry.edge.sizeMm)} mm`,
        from:
          config.edgeTreatment.profile === 'chamfer'
            ? 'teoretický pôvodný ostrý roh'
            : 'stred oblúka zaoblenia',
        to:
          config.edgeTreatment.profile === 'chamfer'
            ? 'bod dotyku skosenia na priľahlej ploche'
            : 'povrch zaoblenej hrany',
        coordinateText:
          config.edgeTreatment.profile === 'chamfer'
            ? `odsadenie E = ${formatNumber(geometry.edge.sizeMm)} mm po oboch plochách`
            : `R = ${formatNumber(geometry.edge.sizeMm)} mm`,
        viewLabel: 'REZ KOLMO NA HRANU',
        exactAnchors: true,
      };
    case 'corner-radius':
      return {
        id,
        label: 'Rádius čelného obrysu X–Y',
        valueText: `${formatNumber(geometry.edge.cornerRadiusMm)} mm`,
        from: 'stred rohového oblúka',
        to: 'tangentný bod na obvode hotového dielu',
        coordinateText: `Rxy = ${formatNumber(geometry.edge.cornerRadiusMm)} mm`,
        viewLabel: 'POHĽAD SPREDU',
        exactAnchors: true,
        note: 'Táto kóta patrí čelnému obrysu X–Y; nie je to rádius hrany v bočnom reze.',
      };
    case 'stock-width':
      return stockMeasurement(
        id,
        'Prídavok k šírke polotovaru',
        config.stockAllowanceMm.widthMm,
        geometry.finished.widthMm,
        geometry.stock.widthMm,
        'W',
        'POHĽAD SPREDU',
      );
    case 'stock-height':
      return stockMeasurement(
        id,
        'Prídavok k výške polotovaru',
        config.stockAllowanceMm.heightMm,
        geometry.finished.heightMm,
        geometry.stock.heightMm,
        'H',
        'POHĽAD SPREDU',
      );
    case 'stock-thickness':
      return stockMeasurement(
        id,
        'Prídavok k hrúbke polotovaru',
        config.stockAllowanceMm.thicknessMm,
        geometry.finished.thicknessMm,
        geometry.stock.thicknessMm,
        'T',
        'BOČNÝ REZ Y–Q',
      );
  }
}

function exactMeasurement(
  id: ConfiguratorMeasurementId,
  label: string,
  valueMm: number,
  from: string,
  to: string,
  coordinateText: string,
  viewLabel: string,
  note?: string,
): ConfiguratorMeasurement {
  return {
    id,
    label,
    valueText: `${formatNumber(valueMm)} mm`,
    from,
    to,
    coordinateText,
    viewLabel,
    exactAnchors: true,
    note,
  };
}

function stockMeasurement(
  id: ConfiguratorMeasurementId,
  label: string,
  allowanceMm: number,
  finishedMm: number,
  stockMm: number,
  axis: string,
  viewLabel: string,
): ConfiguratorMeasurement {
  const allowanceSymbol = axis === 'W' ? 'Ax' : axis === 'H' ? 'Ay' : 'Az';
  return {
    id,
    label,
    valueText: `+${formatNumber(allowanceMm)} mm`,
    from: `hotový rozmer + prídavok ${allowanceSymbol}=${formatNumber(allowanceMm)} mm`,
    to: `celkový rozmer polotovaru ${formatNumber(stockMm)} mm`,
    coordinateText: `${axis}stock = ${axis} + ${allowanceSymbol} = ${formatNumber(finishedMm)} + ${formatNumber(allowanceMm)} = ${formatNumber(stockMm)} mm`,
    viewLabel,
    exactAnchors: false,
    note: 'Dáta určujú iba celkový prídavok. Jeho rozdelenie na konkrétne strany zatiaľ nie je definované.',
  };
}

function axisText(axis: string, [from, to]: [number, number]) {
  return `${axis}=${formatNumber(from)} → ${axis}=${formatNumber(to)} mm`;
}

function handleStartLabel(geometry: ReturnType<typeof deriveDoorGeometry>) {
  if (geometry.handle.openLeft) return 'ľavý otvorený okraj dielu';
  return 'začiatok ľavého nábehu úchytu';
}

function handleEndLabel(geometry: ReturnType<typeof deriveDoorGeometry>) {
  if (geometry.handle.openRight) return 'pravý otvorený okraj dielu';
  return 'koniec pravého nábehu úchytu';
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 1 }).format(value);
}
